import qrcode from 'qrcode-generator';
import { useState, type FormEvent } from 'react';
import { authClient, must } from '../lib/auth.ts';
import { Link, navigate } from '../lib/router.tsx';
import type { Me } from '../lib/api.ts';
import { Brand, ErrorNote, Field, Icon, useAction } from '../ui.tsx';

export function SignIn({ onDone }: { onDone: () => void }) {
  const [mode, setMode] = useState<'passkey' | 'password' | 'code'>('passkey');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(false);
  const { busy, error, run } = useAction();

  const passkey = () =>
    run(async () => {
      await must(authClient.signIn.passkey());
      onDone();
    });

  const withPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const data = (await must(authClient.signIn.email({ email, password }))) as { twoFactorRedirect?: boolean };
      if (data?.twoFactorRedirect) setMode('code');
      else onDone();
    });
  };

  const withCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (backup) await must(authClient.twoFactor.verifyBackupCode({ code: code.trim() }));
      else await must(authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, '') }));
      onDone();
    });
  };

  return (
    <main className="auth">
      <div className="auth-card">
        <Brand />
        <div>
          <h1>Sign in</h1>
          <p className="finding">Your parenting-time record. Only you can open it.</p>
        </div>
        {mode === 'passkey' && (
          <div className="panel form">
            <button className="btn btn-primary btn-wide" onClick={passkey} disabled={busy}>
              <Icon name="key" />
              Sign in with a passkey
            </button>
            <ErrorNote error={error} />
            <p className="hint">
              No passkey on this device?{' '}
              <button className="link" onClick={() => setMode('password')}>
                Use your password and authenticator app
              </button>
            </p>
          </div>
        )}
        {mode === 'password' && (
          <form className="panel form" onSubmit={withPassword}>
            <Field label="Email">
              <input className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Password">
              <input className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <ErrorNote error={error} />
            <button className="btn btn-primary btn-wide" disabled={busy}>
              Continue
            </button>
            <p className="hint">
              <button type="button" className="link" onClick={() => setMode('passkey')}>
                Use a passkey instead
              </button>
            </p>
          </form>
        )}
        {mode === 'code' && (
          <form className="panel form" onSubmit={withCode}>
            <Field label={backup ? 'Backup code' : 'Code from your authenticator app'}>
              <input
                className="input"
                inputMode={backup ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <ErrorNote error={error} />
            <button className="btn btn-primary btn-wide" disabled={busy}>
              Sign in
            </button>
            <p className="hint">
              <button type="button" className="link" onClick={() => setBackup(!backup)}>
                {backup ? 'Use the authenticator app instead' : 'Lost your phone? Use a backup code'}
              </button>
            </p>
          </form>
        )}
        <p className="hint">
          Have an invite code? <Link href="/signup">Create your account</Link>
        </p>
      </div>
    </main>
  );
}

export function SignUp({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({ invite: '', name: '', email: '', password: '', confirm: '' });
  const { busy, error, setError, run } = useAction();
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError('The two passwords don’t match.');
    void run(async () => {
      await must(
        authClient.signUp.email(
          { name: form.name, email: form.email, password: form.password },
          { headers: { 'x-invite-code': form.invite.trim() } },
        ),
      );
      navigate('/', { replace: true });
      onDone();
    });
  };

  return (
    <main className="auth">
      <form className="auth-card" onSubmit={submit}>
        <Brand />
        <div>
          <h1>Create your account</h1>
          <p className="finding">Next you’ll add a passkey and an authenticator app. Your record stays locked until both are set up.</p>
        </div>
        <div className="panel form">
          <Field label="Invite code">
            <input className="input" required autoComplete="off" value={form.invite} onChange={set('invite')} />
          </Field>
          <Field label="Your name">
            <input className="input" required autoComplete="name" value={form.name} onChange={set('name')} />
          </Field>
          <Field label="Email" hint="Used to sign in with your password. Nothing is sent to it yet.">
            <input className="input" type="email" required autoComplete="username" value={form.email} onChange={set('email')} />
          </Field>
          <Field label="Password" hint="At least 12 characters. It’s your backup when no passkey is at hand.">
            <input className="input" type="password" required minLength={12} autoComplete="new-password" value={form.password} onChange={set('password')} />
          </Field>
          <Field label="Password again">
            <input className="input" type="password" required minLength={12} autoComplete="new-password" value={form.confirm} onChange={set('confirm')} />
          </Field>
          <ErrorNote error={error} />
          <button className="btn btn-primary btn-wide" disabled={busy}>
            Create account
          </button>
        </div>
        <p className="hint">
          Already have an account? <Link href="/">Sign in</Link>
        </p>
      </form>
    </main>
  );
}

export function Setup({ me, onDone }: { me: Me; onDone: () => void }) {
  const passkey = useAction();
  const totp = useAction();
  const [password, setPassword] = useState('');
  const [enrol, setEnrol] = useState<{ uri: string; secret: string; codes: string[] } | null>(null);
  const [code, setCode] = useState('');
  const [saved, setSaved] = useState(false);

  const addPasskey = () =>
    passkey.run(async () => {
      await must(authClient.passkey.addPasskey({ name: navigator.platform || 'This device' }));
      onDone();
    });

  const start = (e: FormEvent) => {
    e.preventDefault();
    void totp.run(async () => {
      const data = (await must(authClient.twoFactor.enable({ password }))) as { totpURI: string; backupCodes: string[] };
      const secret = new URL(data.totpURI).searchParams.get('secret') ?? '';
      setEnrol({ uri: data.totpURI, secret, codes: data.backupCodes });
    });
  };

  const verify = (e: FormEvent) => {
    e.preventDefault();
    void totp.run(async () => {
      await must(authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, '') }));
      onDone();
    });
  };

  const qr = (() => {
    if (!enrol) return '';
    const q = qrcode(0, 'M');
    q.addData(enrol.uri);
    q.make();
    return q.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
  })();

  return (
    <main className="auth">
      <div className="auth-card">
        <Brand />
        <div>
          <h1>Secure your account</h1>
          <p className="finding">
            Two steps, once. Afterwards you sign in with the passkey. If it’s ever unavailable, your password and the authenticator code get you in.
          </p>
        </div>
        <div className="steps">
          <div className="step panel" data-done={me.setup.passkey}>
            <span className="step-mark">{me.setup.passkey ? <Icon name="check" size={14} /> : '1'}</span>
            <div className="form">
              <div>
                <b>Add a passkey</b>
                <p className="hint">Uses this device’s fingerprint, face or screen lock. Nothing to remember.</p>
              </div>
              {me.setup.passkey ? (
                <p className="muted">Passkey added.</p>
              ) : (
                <>
                  <button className="btn btn-primary" onClick={addPasskey} disabled={passkey.busy}>
                    <Icon name="key" />
                    Add a passkey
                  </button>
                  <ErrorNote error={passkey.error} />
                </>
              )}
            </div>
          </div>
          <div className="step panel" data-done={me.setup.twoFactor}>
            <span className="step-mark">{me.setup.twoFactor ? <Icon name="check" size={14} /> : '2'}</span>
            <div className="form">
              <div>
                <b>Set up an authenticator app</b>
                <p className="hint">Google Authenticator, 1Password, Authy or similar. It gives the code that goes with your password.</p>
              </div>
              {me.setup.twoFactor ? (
                <p className="muted">Authenticator app set up.</p>
              ) : !enrol ? (
                <form className="form" onSubmit={start}>
                  <Field label="Your password">
                    <input className="input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                  </Field>
                  <ErrorNote error={totp.error} />
                  <button className="btn">Continue</button>
                </form>
              ) : (
                <form className="form" onSubmit={verify}>
                  <p>Scan this with the app, or type the key into it.</p>
                  <div className="qr" dangerouslySetInnerHTML={{ __html: qr }} />
                  <p className="secret">{enrol.secret}</p>
                  <div>
                    <b>Backup codes</b>
                    <p className="hint">Each works once, if you lose your phone. Print them or keep them in a password manager.</p>
                  </div>
                  <div className="codes">
                    {enrol.codes.map((c) => (
                      <span key={c}>{c}</span>
                    ))}
                  </div>
                  <label className="check">
                    <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
                    I’ve saved the backup codes
                  </label>
                  <Field label="Code from the app">
                    <input className="input" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
                  </Field>
                  <ErrorNote error={totp.error} />
                  <button className="btn btn-primary" disabled={!saved || totp.busy}>
                    Turn on
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
        <p className="hint">
          Signed in as {me.user.email}.{' '}
          <button className="link" onClick={() => void authClient.signOut().then(onDone)}>
            Sign out
          </button>
        </p>
      </div>
    </main>
  );
}
